import { useState } from 'react';
import Validator from 'validatorjs';

const getValidationRules = (schema = {}) =>
  Object.entries(schema).reduce((acc, [field, value]) => {
    if (typeof value === 'string') {
      acc[field] = value;
    } else if (value && typeof value === 'object' && value.validation) {
      acc[field] = value.validation;
    }
    return acc;
  }, {});

const getValidationMessages = (schema = {}) =>
  Object.entries(schema).reduce((acc, [field, value]) => {
    if (value && typeof value === 'object' && value.message) {
      Object.entries(value.message).forEach(([rule, msg]) => {
        acc[`${rule}.${field}`] = msg;
      });
    }
    return acc;
  }, {});

export const useForm = ({
  validationSchema,
  initialValues = {},
  customValidationMessage,
  onSubmit = () => {},
}) => {
  const rules = getValidationRules(validationSchema);
  const messages = {
    ...getValidationMessages(validationSchema),
    ...(customValidationMessage || {}),
  };

  const freezedInitialValue = Object.freeze({ ...initialValues });
  const [touched, setTouched] = useState({});
  const [values, setValues] = useState({ ...freezedInitialValue });
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setValues(freezedInitialValue);
  };

  const handleBlur = ({ target: { name, value } }) => {
    setTouched((prevValues) => ({ ...prevValues, [name]: true }));
    const validation = new Validator(
      { ...values, [name]: value },
      rules,
      messages,
    );
    if (validation.fails()) {
      setErrors(validation.errors);
      return;
    }
    setErrors(validation.errors);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(values, { setValues, setErrors, resetForm });
    const touchedFields = {};
    Object.keys(values).forEach((key) => {
      touchedFields[key] = true;
    });

    setTouched(touchedFields);
    if (validationSchema) {
      const validation = new Validator(
        values,
        rules,
        messages,
      );
      
      if (validation.fails()) {
        setErrors(validation.errors);
        return;
      }
      setErrors(validation.errors);
    }
  };

  const handleChange = ({ target: { name, value } }) => {
    setTouched((prevValues) => ({ ...prevValues, [name]: true }));
    setValues((prevValues) => ({ ...prevValues, [name]: value }));
    
    const validation = new Validator(
      { ...values, [name]: value },
      rules,
      messages,
    );
    if (validation.fails()) {
      setErrors(validation.errors);
      return;
    }
    setErrors(validation.errors);
  };

  return {
    handleSubmit,
    handleBlur,
    handleChange,
    touched,
    values,
    initialValues,
    setTouched,
    errors: errors?.errors ?? {},
    setErrors,
    setValues,
  };
};
